import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "antd";
import { FaQuestionCircle, FaExclamationTriangle } from "react-icons/fa";

const MIN_SEAT_SIZE = 0.1;
const MAX_SEAT_SIZE = 99;
const SEAT_STEP = 0.1;
const MIN_REQUIRED_SEATS = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const roundToStep = (value) => Number(value.toFixed(1));

const CalibrationSeatPlacementStep = ({
    onNext,
    onBack,
    onClose,
    imgSrc,
    viewState,
    placedSeats,
    setPlacedSeats,
    seatSize,
    onSeatSizeChange
}) => {
    const previewRef = useRef(null);
    const dragOffsetRef = useRef({ x: 0, y: 0 });

    const [draggingSeatId, setDraggingSeatId] = useState(null);
    const [hoverSeat, setHoverSeat] = useState(null);
    const [isHoveringPlacedSeat, setIsHoveringPlacedSeat] = useState(false);

    const { zoom, offset } = viewState || {
        zoom: 300,
        offset: { x: 0, y: 0 },
    };

    const zoomScale = (zoom || 100) / 100;
    const renderedSeatDiameter = seatSize * zoomScale;

    useEffect(() => {
        const handleWindowMouseMove = (event) => {
            if (draggingSeatId === null || !previewRef.current) return;

            const rect = previewRef.current.getBoundingClientRect();

            const nextX = event.clientX - rect.left - dragOffsetRef.current.x;
            const nextY = event.clientY - rect.top - dragOffsetRef.current.y;

            setPlacedSeats((prevSeats) =>
                prevSeats.map((seat) =>
                    seat.id === draggingSeatId
                        ? { ...seat, x: nextX, y: nextY }
                        : seat
                )
            );
        };

        const handleWindowMouseUp = () => {
            setDraggingSeatId(null);
        };

        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("mouseup", handleWindowMouseUp);
        };
    }, [draggingSeatId, setPlacedSeats]);

    const canContinue = placedSeats.length >= MIN_REQUIRED_SEATS;

    const imageLayerStyle = useMemo(() => {
        return {
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoomScale})`,
            transformOrigin: "top left",
        };
    }, [offset, zoomScale]);

    const previewCursorClass = useMemo(() => {
        if (draggingSeatId !== null) return "is-grabbing";
        if (isHoveringPlacedSeat) return "is-pointer";
        return "is-crosshair";
    }, [draggingSeatId, isHoveringPlacedSeat]);

    const getPointerPosition = (event) => {
        if (!previewRef.current) return null;

        const rect = previewRef.current.getBoundingClientRect();

        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    const handlePreviewMouseMove = (event) => {
        if (draggingSeatId !== null) return;

        const position = getPointerPosition(event);
        if (!position) return;

        setHoverSeat(position);
    };

    const handlePreviewMouseEnter = (event) => {
        if (draggingSeatId !== null) return;

        const position = getPointerPosition(event);
        if (!position) return;

        setHoverSeat(position);
    };

    const handlePreviewMouseLeave = () => {
        if (draggingSeatId !== null) return;
        setHoverSeat(null);
        setIsHoveringPlacedSeat(false);
    };

    const createSeat = (event) => {
        if (!previewRef.current) return;

        const clickedOnSeat = event.target.closest(".calibration-seat-step__seat--placed");
        if (clickedOnSeat) return;

        const position = getPointerPosition(event);
        if (!position) return;

        const newSeat = {
            id: `${Date.now()}-${Math.random()}`,
            x: position.x,
            y: position.y,
        };

        setPlacedSeats((prevSeats) => [...prevSeats, newSeat]);
        setHoverSeat(position);
    };

    const handleSeatMouseDown = (event, seat) => {
        event.stopPropagation();
        event.preventDefault();

        if (!previewRef.current) return;

        const rect = previewRef.current.getBoundingClientRect();

        dragOffsetRef.current = {
            x: event.clientX - rect.left - seat.x,
            y: event.clientY - rect.top - seat.y,
        };

        setDraggingSeatId(seat.id);
        setHoverSeat(null);
        setIsHoveringPlacedSeat(true);
    };

    const handleSeatContextMenu = (event, seatId) => {
        event.preventDefault();
        event.stopPropagation();

        if (draggingSeatId === seatId) {
            setDraggingSeatId(null);
        }

        setPlacedSeats((prevSeats) => prevSeats.filter((seat) => seat.id !== seatId));
    };

    const updateSeatSize = (direction) => {
        onSeatSizeChange((prev) => {
            const nextValue =
                direction === "up" ? prev + SEAT_STEP : prev - SEAT_STEP;

            return roundToStep(clamp(nextValue, MIN_SEAT_SIZE, MAX_SEAT_SIZE));
        });
    };

    const handleSeatSizeInputChange = (event) => {
        const parsed = Number(event.target.value);
        if (Number.isNaN(parsed)) return;

        onSeatSizeChange(parsed);
    };

    const commitSeatSizeInput = () => {
        onSeatSizeChange((prev) => {
            const numericValue = Number(prev);
            const safeValue = Number.isFinite(numericValue)
                ? numericValue
                : MIN_SEAT_SIZE;

            return roundToStep(clamp(safeValue, MIN_SEAT_SIZE, MAX_SEAT_SIZE));
        });
    };

    return (
        <div className="calibration-seat-step">
            <button
                type="button"
                className="calibration-seat-step__close"
                onClick={onClose}
            >
                ✕
            </button>

            <div className="calibration-seat-step__wrapper">
                <div className="calibration-seat-step__header">
                    <div className="calibration-seat-step__badge">2</div>

                    <div className="calibration-seat-step__title-group">
                        <h1>Coloca asientos sobre la imagen</h1>
                        <p>
                            Haz clic para agregar asientos sobre la imagen. Puedes arrastrarlos para
                            ajustarlos a su posición y usar clic derecho para eliminarlos.
                        </p>
                    </div>
                </div>
                <div className="calibration-size-confirm-step__preview-card">
                    <div
                        ref={previewRef}
                        className={`calibration-seat-step__preview ${previewCursorClass}`}
                        onClick={createSeat}
                        onMouseMove={handlePreviewMouseMove}
                        onMouseEnter={handlePreviewMouseEnter}
                        onMouseLeave={handlePreviewMouseLeave}
                    >
                        {imgSrc && (
                            <div
                                className="calibration-seat-step__image-layer"
                                style={imageLayerStyle}
                            >
                                <img
                                    src={imgSrc}
                                    alt="Reference chart"
                                    draggable={false}
                                />
                            </div>
                        )}

                        {hoverSeat && draggingSeatId === null && !isHoveringPlacedSeat && (
                            <div
                                className="calibration-seat-step__seat calibration-seat-step__seat--preview"
                                style={{
                                    width: `${renderedSeatDiameter}px`,
                                    height: `${renderedSeatDiameter}px`,
                                    left: `${hoverSeat.x}px`,
                                    top: `${hoverSeat.y}px`
                                }}
                            >
                            </div>
                        )}

                        {placedSeats.map((seat) => (
                            <button
                                key={seat.id}
                                type="button"
                                className={`calibration-seat-step__seat calibration-seat-step__seat--placed ${draggingSeatId === seat.id ? "is-grabbing" : ""
                                    }`}
                                style={{
                                    width: `${renderedSeatDiameter}px`,
                                    height: `${renderedSeatDiameter}px`,
                                    left: `${seat.x}px`,
                                    top: `${seat.y}px`
                                }}
                                onMouseDown={(event) => handleSeatMouseDown(event, seat)}
                                onContextMenu={(event) =>
                                    handleSeatContextMenu(event, seat.id)
                                }
                                onMouseEnter={() => setIsHoveringPlacedSeat(true)}
                                onMouseLeave={() => {
                                    if (draggingSeatId === null) {
                                        setIsHoveringPlacedSeat(false);
                                    }
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className="calibration-seat-step__bottom">
                    <div className="calibration-seat-step__help">
                        <div className="calibration-seat-step__help-icon">
                            <FaQuestionCircle />
                        </div>

                        <div className="calibration-seat-step__help-content">
                            <h2>Ajusta el tamaño de los asientos</h2>
                            <p>
                                Modifica el tamaño hasta que los asientos colocados coincidan con los de
                                la imagen. La idea es que se superpongan lo mejor posible.
                            </p>
                        </div>
                    </div>

                    <div className="calibration-seat-step__size-box">
                        <span>Tamaño de asiento</span>

                        <div className="calibration-seat-step__size-control">
                            <button
                                type="button"
                                onClick={() => updateSeatSize("down")}
                                disabled={Number(seatSize) <= MIN_SEAT_SIZE}
                            >
                                ‹
                            </button>

                            <span className="calibration-seat-step__size-input-wrapper">
                                <input
                                    type="number"
                                    className="calibration-seat-step__size-input"
                                    value={seatSize}
                                    min={MIN_SEAT_SIZE}
                                    max={MAX_SEAT_SIZE}
                                    step={SEAT_STEP}
                                    onChange={handleSeatSizeInputChange}
                                    onBlur={commitSeatSizeInput}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.currentTarget.blur();
                                        }
                                    }}
                                />
                                <span className="calibration-seat-step__size-unit">pt</span>
                            </span>

                            <button
                                type="button"
                                onClick={() => updateSeatSize("up")}
                                disabled={Number(seatSize) >= MAX_SEAT_SIZE}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </div>

                {!canContinue && (
                    <div className="calibration-seat-step__warning">
                        <FaExclamationTriangle />
                        <span>
                            Debes colocar {MIN_REQUIRED_SEATS} asientos.
                        </span>
                    </div>
                )}

                <div className="calibration-seat-step__actions">
                    <Button type="link" onClick={onBack}>
                        Atras
                    </Button>

                    <Button type="link" onClick={onNext} disabled={!canContinue}>
                        Siguiente
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { CalibrationSeatPlacementStep };